/*
 * sticky-add-to-cart.js — floating PDP bar.
 *
 * Mirrors the Purchase Form's ATC state machine. The Purchase Form
 * is the source of truth: it dispatches `pdp:state-change` with
 * `{ state, variant, productId }` whenever the resolved state
 * updates, and the sticky bar listens at the document level and
 * updates its button label, disabled state, hidden variant input,
 * and data-atc-state attribute accordingly.
 *
 * Click routing (driven by `data-atc-state` on the button):
 *   atc / preorder → let the form submit (goes through
 *     sa-product-form → ajax-cart.js → cart drawer)
 *   oos            → button is disabled, no action
 *   enquiry        → preventDefault, dispatch `pdp:open-enquiry`
 *   notify         → preventDefault, dispatch `pdp:open-notify`
 *
 * Visibility:
 *   `body.sticky-atc--visible` is added when the main product
 *   info has scrolled past the viewport top. An
 *   IntersectionObserver on the footer toggles
 *   `body.footer--in-view` so the sticky bar slides out as the
 *   footer comes into view (CSS rule already in place).
 */

function init() {
  const stickyAtc = document.getElementById('stickyAtc');
  if (!stickyAtc) return;

  const productMain = document.querySelector('.product-main');
  if (!productMain) return;

  const footer =
    document.querySelector('footer') ||
    document.querySelector('[class*="footer-group"]');

  const stickyVariantInput = document.getElementById('sticky-variant-id');
  const button = stickyAtc.querySelector('.sticky-atc__add-btn');
  const label = stickyAtc.querySelector('.sticky-atc__add-btn-label');

  // ---------- Visibility (scroll-based) -----------------------
  function checkStickyVisibility() {
    const mainBottom = productMain.getBoundingClientRect().bottom;
    const vh = window.innerHeight;
    if (mainBottom < vh) {
      document.body.classList.add('sticky-atc--visible');
    } else {
      document.body.classList.remove('sticky-atc--visible');
    }
  }
  window.addEventListener('scroll', checkStickyVisibility, { passive: true });
  window.addEventListener('resize', checkStickyVisibility);
  checkStickyVisibility();

  // ---------- Footer in-view (IntersectionObserver) -----------
  // `body.footer--in-view` is the override the CSS uses to slide
  // the sticky bar back out as the footer enters the viewport.
  // IO is more reliable than getBoundingClientRect polls and
  // doesn't compete with the scroll handler above.
  if (footer && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            document.body.classList.add('footer--in-view');
          } else {
            document.body.classList.remove('footer--in-view');
          }
        });
      },
      { threshold: 0 }
    );
    io.observe(footer);
  }

  // ---------- Mode branch -------------------------------------
  // The merchant's Button Function setting drives this. In
  // scroll-to-top mode the bar has no form and no submit — the
  // click just smooth-scrolls the window back up. Even so, we
  // still mirror the Purchase Form's state onto the button label
  // (no type / disabled / class changes, only the visible text)
  // so the sticky bar advertises the action the reader would land
  // on if they scrolled up. The full state machine below (form
  // submit routing, disabled state, dispatched modal-open events)
  // is skipped for this mode via the `return` at the end.
  const mode = stickyAtc.dataset.stickyMode || 'atc';
  if (mode === 'scroll_to_top') {
    if (button) {
      button.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    // Labels mirror the main Purchase Form's state map exactly,
    // MINUS the trailing " - <price>" appended to atc / preorder.
    // The sticky bar's job is only to advertise the action, not
    // the amount, so we drop the price segment here. Text of
    // every other state matches Purchase Form byte-for-byte.
    // Source of truth: assets/product-purchase-form.js
    // `_applyState`.
    const scrollLabelForState = (state) => {
      switch (state) {
        case 'enquiry':
          return 'Make an enquiry';
        case 'notify':
          return 'Out of stock - Notify me';
        case 'oos':
          return 'Out of stock';
        case 'preorder':
          return 'Pre order';
        case 'atc':
        default:
          return 'Add to cart';
      }
    };

    const syncScrollLabel = (state) => {
      if (label) label.textContent = scrollLabelForState(state);
      if (button) button.dataset.atcState = state;
    };

    // Purchase Form's fuller state (enquiry / notify / preorder /
    // atc / oos) wins where available.
    document.addEventListener('pdp:state-change', (event) => {
      const detail = event.detail || {};
      const variant = detail.variant;
      if (!variant) return;
      syncScrollLabel(detail.state || (variant.available ? 'atc' : 'oos'));
    });

    // H3.0 native picker fallback — no state machine, just derive
    // atc / oos from `variant.available`. Fires alongside Purchase
    // Form's `pdp:state-change` on shared setters; the fuller
    // state wins by last-write ordering.
    document.addEventListener('variant:update', (event) => {
      const variant = event?.detail?.resource;
      if (!variant) return;
      syncScrollLabel(variant.available ? 'atc' : 'oos');
    });

    // Late-subscriber handshake (same rationale as the ATC branch
    // further down) — the Purchase Form's initial state-broadcast
    // fires during its own construction, potentially before this
    // listener attached, so ping it to re-broadcast the current
    // state now that we're listening.
    queueMicrotask(() => {
      document.dispatchEvent(new CustomEvent('pdp:request-state'));
    });

    return;
  }

  // ---------- State mirror ------------------------------------
  // Apply a resolved state to the sticky bar button. Mirrors the
  // Purchase Form's _applyState shape so the two stay visually
  // consistent (same labels, same modifier classes).
  function applyState(state, variant) {
    if (!button) return;

    button.classList.remove(
      'is-sold-out',
      'is-preorder',
      'enquiry-mode',
      'bis-mode'
    );
    button.dataset.atcState = state;

    // Labels mirror the main Purchase Form's state map byte-for-
    // byte MINUS the trailing " - <price>" appended to atc /
    // preorder. Sticky bar's job is to advertise the action, not
    // the amount. Source of truth: assets/product-purchase-form.js
    // `_applyState`.
    switch (state) {
      case 'enquiry':
        if (label) label.textContent = 'Make an enquiry';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('enquiry-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'notify':
        if (label) label.textContent = 'Out of stock - Notify me';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('bis-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'oos':
        if (label) label.textContent = 'Out of stock';
        button.type = 'button';
        button.disabled = true;
        button.classList.add('is-sold-out');
        break;

      case 'preorder':
        if (label) label.textContent = 'Pre order';
        button.type = 'submit';
        button.disabled = false;
        button.classList.add('is-preorder');
        break;

      case 'atc':
      default:
        if (label) label.textContent = 'Add to cart';
        button.type = 'submit';
        button.disabled = false;
        break;
    }
  }

  // ---------- pdp:state-change listener -----------------------
  // Document-level so it works regardless of whether the
  // Purchase Form is in the same section (PDP grid) or a sibling
  // section. Filter by productId to avoid cross-product bleed
  // when a future quick-add lands on the same page.
  document.addEventListener('pdp:state-change', (event) => {
    const detail = event.detail || {};
    const variant = detail.variant;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(detail.state || (variant.available ? 'atc' : 'oos'), variant);
  });

  // ---------- variant:update fallback -------------------------
  // When the PDP uses H3.0's native variant-picker block instead
  // of (or in addition to) the SA Purchase Form, the picker
  // dispatches `variant:update` directly without going through
  // our state machine. The sticky bar can still derive a basic
  // atc/oos state from `variant.available` so the label doesn't
  // go stale. If Purchase Form is also present, its
  // `pdp:state-change` fires AFTER variant:update in the same
  // `_setCurrentVariant` call — so the fuller state wins
  // naturally (last-write).
  document.addEventListener('variant:update', (event) => {
    const variant = event?.detail?.resource;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(variant.available ? 'atc' : 'oos', variant);
  });

  // ---------- Click routing -----------------------------------
  // Match the Purchase Form's _bindAtcStateClicks: enquiry/notify
  // states preventDefault + dispatch their modal-open event;
  // atc/preorder fall through to natural form submit; oos is
  // disabled so click won't fire anyway.
  if (button) {
    button.addEventListener('click', (event) => {
      const state = button.dataset.atcState;
      if (state !== 'enquiry' && state !== 'notify') return;
      event.preventDefault();

      const variantId = stickyVariantInput?.value;
      const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
      button.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: {
            variant: variantId ? { id: Number(variantId) } : null,
          },
        })
      );
    });
  }

  // ---------- Initial variant id sync -------------------------
  // Seed the sticky bar's hidden input from the main product
  // form's id input immediately so the SSR markup is in sync
  // before any event arrives.
  const mainVariantInput = productMain.querySelector('input[name="id"]');
  if (mainVariantInput && stickyVariantInput) {
    stickyVariantInput.value = mainVariantInput.value;
  }

  // ---------- Late-subscriber handshake -----------------------
  // The Purchase Form dispatches its initial `pdp:state-change`
  // during construction (also at DCL), BEFORE the listener
  // above was attached — so the first paint of the sticky bar
  // is stuck on the SSR'd atc/oos state, missing Made-to-Order
  // (Enquiry), Pre-Order, and Notify Me. Pinging
  // `pdp:request-state` makes the Purchase Form re-broadcast
  // its current state so we catch up immediately. Fire on the
  // next microtask so all listeners on the page (this one
  // included) are guaranteed registered before the re-fire
  // arrives.
  queueMicrotask(() => {
    document.dispatchEvent(new CustomEvent('pdp:request-state'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
